import { isNotNullish } from 'effect/Predicate'
import { v4 as uuidv4 } from 'uuid'

import type { FilterFormItemState, ViewConfigFilterNodeForForm } from './common'

/**
 * フィルタツリーのDnD用に、各ノード（リーフ・グループ）に振る一意キー。
 * 永続化はせず、フォーム状態の中だけで保持する。
 */
// eslint-disable-next-line sonarjs/redundant-type-aliases
export type FilterTreeNodeId = string

export interface FilterFormItemStateWithId extends FilterFormItemState {
  readonly _id: FilterTreeNodeId
}

export interface ViewConfigFilterNodeForFormWithId {
  readonly _id: FilterTreeNodeId
  readonly logicalOperator: 'and' | 'or'
  readonly leafs: readonly FilterFormItemStateWithId[]
  readonly children: readonly ViewConfigFilterNodeForFormWithId[]
}

/** フォーム状態用の一意ノード id を新規生成する。id の生成方式はこの1関数に集約する。 */
export const newFilterTreeNodeId = (): FilterTreeNodeId => uuidv4()

/** ツリー全体（ノード・リーフ）に `_id` を新規付与する。 */
export const assignIds = (node: ViewConfigFilterNodeForForm): ViewConfigFilterNodeForFormWithId => ({
  _id: newFilterTreeNodeId(),
  logicalOperator: node.logicalOperator,
  leafs: node.leafs.map((leaf) => ({ ...leaf, _id: newFilterTreeNodeId() })),
  children: node.children.map((child) => assignIds(child)),
})

/** ツリーから `_id` を取り除き、永続化用の型に戻す。 */
export const stripIds = (node: ViewConfigFilterNodeForFormWithId): ViewConfigFilterNodeForForm => ({
  logicalOperator: node.logicalOperator,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- _id を除いた残りを永続化用に取り出すための分割代入
  leafs: node.leafs.map(({ _id, ...rest }) => rest),
  children: node.children.map((child) => stripIds(child)),
})

/** 新しい空のグループノードを作る。 */
export const createEmptyGroup = (): ViewConfigFilterNodeForFormWithId => ({
  _id: newFilterTreeNodeId(),
  logicalOperator: 'and',
  leafs: [],
  children: [],
})

interface LeafLocation {
  readonly kind: 'leaf'
  readonly parent: ViewConfigFilterNodeForFormWithId
  readonly index: number
  readonly leaf: FilterFormItemStateWithId
}

interface GroupLocation {
  readonly kind: 'group'
  readonly parent: ViewConfigFilterNodeForFormWithId | undefined // ルートのみ undefined
  readonly index: number
  readonly group: ViewConfigFilterNodeForFormWithId
}

type NodeLocation = LeafLocation | GroupLocation

/** 与えられた id を持つリーフ/グループの場所を返す（深さ優先探索）。 */
export const findLocation = (
  root: ViewConfigFilterNodeForFormWithId,
  id: FilterTreeNodeId,
): NodeLocation | undefined => {
  if (root._id === id) {
    return { kind: 'group', parent: undefined, index: -1, group: root }
  }
  const walk = (node: ViewConfigFilterNodeForFormWithId): NodeLocation | undefined => {
    const leaf = node.leafs.find((x) => x._id === id)
    if (leaf !== undefined) {
      return { kind: 'leaf', parent: node, index: node.leafs.indexOf(leaf), leaf }
    }
    const child = node.children.find((x) => x._id === id)
    if (child !== undefined) {
      return { kind: 'group', parent: node, index: node.children.indexOf(child), group: child }
    }
    return node.children.map((x) => walk(x)).find(isNotNullish)
  }
  return walk(root)
}

/** あるグループが別グループの子孫かどうか（移動先が自身の子孫の場合の循環防止用）。 */
const isDescendant = (
  ancestor: ViewConfigFilterNodeForFormWithId,
  candidate: ViewConfigFilterNodeForFormWithId,
): boolean => {
  if (ancestor._id === candidate._id) return true
  return ancestor.children.some((child) => isDescendant(child, candidate))
}

/** 与えられたグループの位置だけを transform で差し替える（イミュータブル更新）。 */
export const updateGroupById = (
  root: ViewConfigFilterNodeForFormWithId,
  targetId: FilterTreeNodeId,
  transform: (node: ViewConfigFilterNodeForFormWithId) => ViewConfigFilterNodeForFormWithId,
): ViewConfigFilterNodeForFormWithId => {
  if (root._id === targetId) {
    return transform(root)
  }
  return { ...root, children: root.children.map((child) => updateGroupById(child, targetId, transform)) }
}

/** index を [0, length] にクランプしてイミュータブルに挿入する。 */
const insertAt = <T>(items: readonly T[], item: T, index: number): T[] => {
  const clamped = Math.max(0, Math.min(index, items.length))
  return [...items.slice(0, clamped), item, ...items.slice(clamped)]
}

/** `_id` が一致する要素を除いた配列を返す。 */
const removeById = <T extends { readonly _id: FilterTreeNodeId }>(items: readonly T[], id: FilterTreeNodeId): T[] =>
  items.filter((item) => item._id !== id)

const removeLeafFrom = (
  group: ViewConfigFilterNodeForFormWithId,
  leafId: FilterTreeNodeId,
): ViewConfigFilterNodeForFormWithId => ({ ...group, leafs: removeById(group.leafs, leafId) })

const removeChildFrom = (
  group: ViewConfigFilterNodeForFormWithId,
  childId: FilterTreeNodeId,
): ViewConfigFilterNodeForFormWithId => ({ ...group, children: removeById(group.children, childId) })

const insertLeafInto = (
  group: ViewConfigFilterNodeForFormWithId,
  leaf: FilterFormItemStateWithId,
  index: number,
): ViewConfigFilterNodeForFormWithId => ({ ...group, leafs: insertAt(group.leafs, leaf, index) })

const insertChildInto = (
  group: ViewConfigFilterNodeForFormWithId,
  child: ViewConfigFilterNodeForFormWithId,
  index: number,
): ViewConfigFilterNodeForFormWithId => ({ ...group, children: insertAt(group.children, child, index) })

/**
 * リーフ移動。同一親内の並び替えと、別親への移動の両方に対応する。
 * 設定値（property/filterType/filterValue 等）は維持される（UC1〜UC4）。
 */
export const moveLeaf = ({
  root,
  leafId,
  targetParentId,
  targetIndex,
}: {
  readonly root: ViewConfigFilterNodeForFormWithId
  readonly leafId: FilterTreeNodeId
  readonly targetParentId: FilterTreeNodeId
  readonly targetIndex: number
}): ViewConfigFilterNodeForFormWithId => {
  const source = findLocation(root, leafId)
  if (source?.kind !== 'leaf') return root
  const sourceParentId = source.parent._id

  // 同一親内での並び替えは、一旦除いてから targetIndex に挿し直す（除去後の配列基準で index 解釈）
  if (sourceParentId === targetParentId) {
    return updateGroupById(root, targetParentId, (parent) =>
      insertLeafInto(removeLeafFrom(parent, leafId), source.leaf, targetIndex),
    )
  }
  const removed = updateGroupById(root, sourceParentId, (parent) => removeLeafFrom(parent, leafId))
  return updateGroupById(removed, targetParentId, (parent) => insertLeafInto(parent, source.leaf, targetIndex))
}

/**
 * グループ移動。同一階層の並び替えと、別の親への移動の両方に対応する（UC5〜UC7）。
 * ルートそのもの・自身の子孫への移動は無視する（循環防止）。
 */
export const moveGroup = ({
  root,
  groupId,
  targetParentId,
  targetIndex,
}: {
  readonly root: ViewConfigFilterNodeForFormWithId
  readonly groupId: FilterTreeNodeId
  readonly targetParentId: FilterTreeNodeId
  readonly targetIndex: number
}): ViewConfigFilterNodeForFormWithId => {
  if (groupId === root._id) return root
  const source = findLocation(root, groupId)
  if (source?.kind !== 'group' || source.parent === undefined) return root

  const targetLocation = findLocation(root, targetParentId)
  if (targetLocation?.kind !== 'group') return root
  // 移動先が自分自身、もしくは自分の子孫の場合は循環するため no-op
  if (isDescendant(source.group, targetLocation.group)) return root

  const sourceParentId = source.parent._id
  if (sourceParentId === targetParentId) {
    return updateGroupById(root, targetParentId, (parent) =>
      insertChildInto(removeChildFrom(parent, groupId), source.group, targetIndex),
    )
  }
  const removed = updateGroupById(root, sourceParentId, (parent) => removeChildFrom(parent, groupId))
  return updateGroupById(removed, targetParentId, (parent) => insertChildInto(parent, source.group, targetIndex))
}
